import { ReactNode, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FilePreview from "files/components/FilePreview";
import FileTitle from "files/components/FileTitle";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import useFiles, { useCurrentKey, useFilesInfinite, useFilesSearch } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileInfo from "files/types/interfaces/fileInfo";
import { FolderPlus, Upload } from "lucide-react";
import queryString from "query-string";
import { BooleanParam, StringParam, useQueryParam, useQueryParams } from "use-query-params";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Directory as ShadcnDirectory } from "~/components/ui/directory";
import { CreateFolderDialog } from "~/pages/manifold-nextgen/components/files/CreateFolderDialog";
import { UploadFileDialog } from "~/pages/manifold-nextgen/components/files/UploadFileDialog";
import EmbeddedContextualSearch from "./EmbeddedContextualSearch";
import { EmbeddedFileBreadcrumbs } from "./EmbeddedFileBreadcrumbs";
import { EmbeddedFileJobs } from "./EmbeddedFileJobs";
import { EmbeddedFileNotFound } from "./EmbeddedFileNotFound";
import { EmbeddedSearchResults } from "./EmbeddedSearchResults";

const SearchTitle = ({ children }: { children: ReactNode }) => (
  <h1 className="text-xl leading-[1.2] font-semibold [overflow-wrap:break-word]">{children}</h1>
);

const EmbeddedFilesHeader = () => {
  const basePath = useBasePath();
  const navigate = useNavigate();
  const currentKey = useCurrentKey();
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const canWrite = useFileAccess(currentKey, "write");
  const showUpload = canWrite && currentKey !== BaseDirectory.ORG;
  const { isLoading: searchLoading } = useFilesSearch();
  const [{ q: query, preview }] = useQueryParams({ q: StringParam, preview: BooleanParam });
  const searchDirLabel = <FileTitle fileKey={currentKey} />;

  const title = useMemo(() => {
    if (query) {
      if (!currentKey) {
        return <SearchTitle>Results</SearchTitle>;
      }
      return <SearchTitle>Results in &quot;{searchDirLabel}&quot;</SearchTitle>;
    }
    return <EmbeddedFileBreadcrumbs />;
  }, [query, preview, currentKey]);

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 pt-4 sm:px-6">
      <div className="flex min-h-10 items-center justify-between gap-2">
        {title}
        <div className="flex items-center gap-2">
          <EmbeddedFileJobs />
          {showUpload && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setNewFolderDialogOpen(true)}
              >
                <FolderPlus />
                New Folder
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary/80"
                onClick={() => setUploadDialogOpen(true)}
              >
                <Upload />
                Upload Files
              </Button>
            </>
          )}
        </div>
      </div>
      <div className="mt-2 mb-4">
        <EmbeddedContextualSearch
          loading={searchLoading}
          currentPathLabel={currentKey && searchDirLabel}
          submitCallback={({ query, root }) => {
            const qs = queryString.stringify({ q: query });

            if (root) {
              navigate({ pathname: basePath, search: qs });
              return;
            }
          }}
        />
      </div>
      <CreateFolderDialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
      />
      <UploadFileDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} />
    </div>
  );
};

/**
 * Shadcn rebuild of `files/components/Files`'s breadcrumbs/toolbar for the
 * nextgen-embedded Files tab only — everything below the header (search
 * results, directory listing, empty state, preview) is unchanged, still the
 * same shared pieces the standalone Research Workbench app uses.
 */
export const EmbeddedFiles = () => {
  const { state } = useLocation();
  const preview = state?.preview as Pick<FileInfo, "key" | "size"> | undefined;
  const [search] = useQueryParam("q", StringParam);
  const {
    data: files,
    allChildren,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error: getFileListError,
  } = useFilesInfinite();

  const body = useMemo(() => {
    if (getFileListError) {
      return (
        <Alert variant="destructive">
          <AlertDescription>Unable to load files</AlertDescription>
        </Alert>
      );
    }

    if (search) {
      return <EmbeddedSearchResults />;
    }

    if (!isLoading && !files?.current_file) {
      return <EmbeddedFileNotFound />;
    }

    return (
      <ShadcnDirectory
        navigation="url-based"
        files={allChildren}
        loading={isLoading}
        showExternalBuckets
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />
    );
  }, [files, allChildren, search, getFileListError, isLoading, hasMore, isLoadingMore, loadMore]);

  return (
    <div className="h-full min-w-[750px] overflow-auto bg-background">
      <EmbeddedFilesHeader />
      <div className="mx-auto w-full max-w-[1200px] px-4 pb-6 sm:px-6">{body}</div>
      {preview && <FilePreview file={preview} />}
    </div>
  );
};
